"use client";

import React, { useEffect, useRef, useState } from "react";
import * as s from "@/lib/styles";
import { ChevronLeft, ChevronRight, CirclePlus, Spinner } from "@/lib/icons";
import { Modal } from "@/components/modal";
import { SpreadPnlChart } from "@/components/spread-pnl-chart";
import { CompositePnlModal } from "@/components/composite-pnl-modal";
import { apiGet, apiPost, type UiAccount } from "@/lib/api";
import { accountNickname, livePositionsUrl, strategyAccountName } from "@/lib/account-name";
import {
  cashTicker, contractKeyOf, daysToExpirationBs, isWorkingVsCloseOrder, occIdentity, occRootOf, optionStrikeExpiration, parseOptionSymbol,
} from "@/lib/parse-utils";
import { classifyPositions, type ClassifiedEquity, type ClassifiedOption, type SpreadGroup } from "@/lib/classify-positions";
import { spreadFromTrade, tradeFromBrokerSpread } from "@/lib/vs-open-from-broker";
import { scheduleForAccount, type FeeSchedule } from "@/lib/fees";
import { confirmCancelOrder } from "@/lib/confirms";
import { useConfirm } from "@/components/confirm-provider";
import { useToast } from "@/components/toast-provider";
import { OPTION_MULTIPLIER } from "@/lib/option-multiplier";
import { netSpreadTheta } from "@/lib/spread-theta";
import { calcBuybackTarget } from "@/app/strategies/components/take-profit";
import * as vsCfg from "@/app/strategies/vertical-spreads/lib/config";
import * as vs0Cfg from "@/app/strategies/0dte-vertical-spreads/lib/config";
import * as vseCfg from "@/app/strategies/equity-vertical-spreads/lib/config";
import { buyMoreSpread as vsBuyMore } from "@/app/strategies/vertical-spreads/lib/buy-more";
import { buyMoreSpread as vs0BuyMore } from "@/app/strategies/0dte-vertical-spreads/lib/buy-more";
import { buyMoreSpread as vseBuyMore } from "@/app/strategies/equity-vertical-spreads/lib/buy-more";
import * as vsModals from "@/app/strategies/vertical-spreads/trade-modals";
import * as vs0Modals from "@/app/strategies/0dte-vertical-spreads/trade-modals";
import * as vseModals from "@/app/strategies/equity-vertical-spreads/trade-modals";
import { ReplaceOrderModal } from "@/app/strategies/covered-calls/trade-modals";
import { useMarketIndices } from "@/lib/use-market-indices";
import { BrokerLogo } from "@/app/strategies/components/strategy-page-header";
import type { AccountDetail, OpenOrder, VsLiveData, VsTrade } from "@/app/strategies/vertical-spreads/types";
import type { VseTrade } from "@/app/strategies/equity-vertical-spreads/types";
import { makeSpreadOverlay, type SpreadChartOverlay } from "@/components/price-chart";
import {
  ColumnTable, ageDteColumn, bamlColumn, codeColumn, dteColumn, eProbItmColumn, expDateColumn, exitOrderColumn, hvPctColumn,
  incPremColumn, itmColumn, ivHvColumn, kindTone, priceColumn, probItmColumn, qtyColumn, renderPctPairColored, renderPlainPair,
  strikesColumn, symbolColumn, typeColumn, type TableColumn,
} from "@/app/trades/summary-columns";

/**
 * The strategy ledgers whose open spreads the strategy pages list: open-row
 * codes, and the config that holds each page's take-profit tiers.
 */
const LEDGERS = [
  { id: "vs", api: "/vs", put: "CPSO", call: "CCSO", config: vsCfg.loadVsConfig, tiers: "vs_profit_capture_tiers" },
  { id: "vs0", api: "/vs0", put: "ZPSO", call: "ZCSO", config: vs0Cfg.loadVsConfig, tiers: "vs0_profit_capture_tiers" },
  { id: "vse", api: "/vse", put: "CPSO", call: "CCSO", config: vseCfg.loadVseConfig, tiers: "vse_profit_capture_tiers" },
] as const;

type LedgerId = (typeof LEDGERS)[number]["id"];

/** Net quote for a spread, combined from its two legs exactly as the strategy pages do. */
type SpreadQuote = VsLiveData;

type LegLive = {
  bid: number; ask: number; last: number; mid: number;
  iv: number | null;
  probItm: number | null; openInterest: number | null;
  underlyingPrice: number;
  avgDailyVolume: number | null; marketCap: number | null;
  hvValue: number | null; hvPct: number | null;
};

/** A ledger spread with everything its strategy page shows next to it. */
interface HeldSpread {
  trade: VsTrade;
  /** The strategy whose ledger holds the spread — its order windows handle the buttons. */
  ledger: LedgerId;
  /** That strategy's settings for the account. */
  cfg: Record<string, string>;
  /** The spread as the strategy page's close ticket takes it. */
  group: SpreadGroup & { trade: VsTrade };
  code: string;
  quote: SpreadQuote | null;
  closeOrder: OpenOrder | null;
  /** Take-profit target from the strategy's tiers, shown when no exit order is working. */
  targetPrice: number | null;
  entryEp: number | null;
  ep: number | null;
  pp: number | null;
}

/** One account's open positions: ledger spreads (strategy-page numbers) plus whatever else the broker holds. */
interface Held {
  account: UiAccount;
  /** Account value the strategy pages size new orders from. */
  accountValue: number;
  feeSchedule: FeeSchedule;
  orders: OpenOrder[];
  /** Contract keys of held long / short option legs (opening-order conflict check). */
  longHeld: Set<string>;
  shortHeld: Set<string>;
  spreads: HeldSpread[];
  options: ClassifiedOption[];
  equities: ClassifiedEquity[];
}

type Batch = { key: string; ticker: string; dte: number; strike: number; side: string; spot?: number };

/** Run a probability batch; returns key → value (null when the endpoint had no answer). */
async function batch(path: string, field: "p_hat" | "pItm", positions: Batch[]): Promise<Record<string, number | null>> {
  if (positions.length === 0) return {};
  const r = await apiPost<{ results: ({ key: string; ok: boolean } & Record<string, number>)[] }>(path, { positions })
    .catch(() => ({ results: [] }));
  const out: Record<string, number | null> = {};
  for (const x of r.results) out[x.key] = x.ok ? x[field] : null;
  return out;
}

/** Today's ET date at local midnight, as the strategy pages measure DTE. */
function todayETMidnight(): number {
  const nowET = new Date(new Date().toLocaleString("en-US", { timeZone: "America/New_York" }));
  return new Date(nowET.getFullYear(), nowET.getMonth(), nowET.getDate()).getTime();
}

interface Row {
  key: string;
  account: UiAccount;
  held: Held;
  code: string;
  underlying: string;
  occRoot: string;
  /** null for stock. */
  isPut: boolean | null;
  expDate: string | null;
  openedAt: string | null;
  shortStrike: number | null;
  longStrike: number | null;
  shortSymbol: string | null;
  longSymbol: string | null;
  /** Signed size: negative = short. Contracts for options, shares for stock. */
  qty: number;
  /** Entry price per share (net credit for a spread). */
  price: number;
  quote: SpreadQuote | null;
  /** The ledger spread behind the row; null for single options and stock. */
  spread: HeldSpread | null;
  /** Mid within $0.05 of the exit order's limit: about to fill (green row). */
  nearExit: boolean;
  /** P(ITM) over 25% and theta more than doubled since entry (red row). */
  alert: boolean;
  /** Broker mark per share, for rows that are not ledger spreads. */
  mark: number | null;
  underlyingPrice: number | null;
  income: number | null;
  premium: number | null;
}

async function brokerPositions(account: UiAccount): Promise<{ equities: ClassifiedEquity[]; options: ClassifiedOption[]; accountValue: number }> {
  const url = livePositionsUrl(account);
  // Paper accounts are valued at their settled cash, as on the strategy pages.
  if (!url || account.paper) return { equities: [], options: [], accountValue: account.cashBalance ?? 0 };
  const data = await apiGet<AccountDetail>(url);
  const raw = data.securitiesAccount?.positions ?? [];
  const bal = data.securitiesAccount?.currentBalances;
  const equities: ClassifiedEquity[] = [];
  const options: ClassifiedOption[] = [];
  for (const p of raw) {
    const qty = p.shortQuantity || p.longQuantity;
    if (!(qty > 0)) continue;
    if (p.instrument.assetType === "EQUITY") {
      // Shorts carry a negative share count.
      equities.push({
        symbol: p.instrument.symbol,
        shares: p.shortQuantity > 0 ? -qty : qty,
        avgCost: p.averagePrice || 0,
        marketValue: p.marketValue || 0,
      });
      continue;
    }
    if (p.instrument.assetType !== "OPTION" || !p.instrument.underlyingSymbol) continue;
    const { strike, expiration } = optionStrikeExpiration(p.instrument.symbol, p.instrument.description);
    const occ = parseOptionSymbol(p.instrument.symbol);
    options.push({
      optionSymbol: p.instrument.symbol,
      underlying: cashTicker(p.instrument.underlyingSymbol),
      putCall: p.instrument.putCall || (occ?.putCall === "P" ? "PUT" : occ?.putCall === "C" ? "CALL" : ""),
      description: p.instrument.description,
      strike,
      expiration,
      contracts: qty,
      isShort: p.shortQuantity > 0,
      averagePrice: p.averagePrice || 0,
      marketValue: p.marketValue || 0,
    });
  }
  return { equities, options, accountValue: bal?.liquidationValue ?? bal?.cashBalance ?? 0 };
}

async function loadHeld(account: UiAccount, spotOf: (sym: string) => number | null): Promise<Held> {
  const hash = account.hashValue;
  const [broker, orders, feeSchedule] = await Promise.all([
    brokerPositions(account),
    apiGet<OpenOrder[]>(`/options/orders?accountHash=${encodeURIComponent(hash)}&tokenId=${account.tokenId}`).catch(() => [] as OpenOrder[]),
    apiGet<unknown>(`/fees/for-account/${encodeURIComponent(hash)}`)
      .then((raw) => scheduleForAccount(hash, raw))
      .catch(() => scheduleForAccount(hash, null)),
  ]);
  const { spreads: brokerSpreads } = classifyPositions([], broker.options);

  // Open ledger spreads, one ledger at a time: each read also records new
  // broker fills for the account, and those must not run side by side.
  const opens: { trade: VsTrade; code: string; ledger: LedgerId; cfg: Record<string, string>; group: HeldSpread["group"] }[] = [];
  const seen = new Set<string>();
  for (const l of LEDGERS) {
    const [trades, cfg] = await Promise.all([
      apiGet<VsTrade[]>(`${l.api}/trades?account=${encodeURIComponent(hash)}`).catch(() => [] as VsTrade[]),
      l.config(hash).catch(() => ({} as Record<string, string>)),
    ]);
    // A ledger keeps every one of its rows (two entries of one spread are two
    // rows); a spread an earlier ledger already listed is not repeated.
    const fresh = trades.filter((t) => t.status === "open" && !seen.has(occIdentity(t.shortOptionSymbol)));
    // The strategy page ties a live broker spread to the last ledger row on its
    // short contract; every other row closes from its own legs.
    const lastByContract = new Map<string, number>();
    for (const t of trades) if (t.status === "open") lastByContract.set(contractKeyOf(t.symbol, t.shortOptionSymbol), t.id);
    for (const t of fresh) {
      const broker = lastByContract.get(contractKeyOf(t.symbol, t.shortOptionSymbol)) === t.id
        ? brokerSpreads.find((sp) =>
          occIdentity(sp.shortLeg.optionSymbol) === occIdentity(t.shortOptionSymbol)
          && occIdentity(sp.longLeg.optionSymbol) === occIdentity(t.longOptionSymbol))
        : undefined;
      opens.push({
        trade: t,
        code: t.spreadType === "bull_put" ? l.put : l.call,
        ledger: l.id,
        cfg,
        group: broker ? { ...broker, trade: t } : spreadFromTrade(t),
      });
    }
    for (const t of fresh) seen.add(occIdentity(t.shortOptionSymbol));
  }
  // Broker spreads not in any ledger yet, built the way the Vertical Spreads page builds them.
  const extra = brokerSpreads.filter((sp) => !seen.has(occIdentity(sp.shortLeg.optionSymbol)));
  if (extra.length > 0) {
    const cfg = await vsCfg.loadVsConfig(hash).catch(() => ({} as Record<string, string>));
    for (const sp of extra) {
      seen.add(occIdentity(sp.shortLeg.optionSymbol));
      const trade = tradeFromBrokerSpread<VsTrade>(sp, hash);
      opens.push({
        trade,
        code: sp.putCall === "PUT" ? "CPSO" : "CCSO",
        ledger: "vs",
        cfg,
        group: spreadFromTrade(trade),
      });
    }
  }
  // Every option leg already shown as a spread leaves the single-option list.
  const inSpread = new Set(opens.flatMap(({ trade: t }) => [occIdentity(t.shortOptionSymbol), occIdentity(t.longOptionSymbol)]));
  const options = broker.options.filter((o) => !inSpread.has(occIdentity(o.optionSymbol)));

  // Live leg quotes, combined per spread.
  const legs = opens.length === 0 ? {} : await apiPost<Record<string, LegLive>>("/options/live", {
    trades: opens.flatMap(({ trade: t }) => [
      { underlying: t.symbol, optionSymbol: t.shortOptionSymbol, expirationDate: t.expirationDate, strikePrice: t.shortStrikePrice, contracts: t.contracts },
      { underlying: t.symbol, optionSymbol: t.longOptionSymbol, expirationDate: t.expirationDate, strikePrice: t.longStrikePrice, contracts: t.contracts },
    ]),
    accountHash: hash,
  }).catch(() => ({} as Record<string, LegLive>));
  const quotes = opens.map(({ trade: t }): SpreadQuote | null => {
    const sh = legs[t.shortOptionSymbol];
    const lo = legs[t.longOptionSymbol];
    if (!sh || !lo) return null;
    const netMid = sh.mid - lo.mid;
    return {
      shortBid: sh.bid, shortAsk: sh.ask, shortMid: sh.mid, shortLast: sh.last > 0 ? sh.last : null,
      longBid: lo.bid, longAsk: lo.ask, longMid: lo.mid, longLast: lo.last > 0 ? lo.last : null,
      netBid: sh.bid - lo.ask,
      netAsk: sh.ask - lo.bid,
      netMid,
      netLast: sh.last > 0 && lo.last > 0 ? sh.last - lo.last : null,
      shortIv: sh.iv,
      longIv: lo.iv,
      // The index feed's cash price wins over the chain's, as on the strategy pages.
      underlyingPrice: spotOf(t.symbol) ?? sh.underlyingPrice,
      totalValue: netMid * t.contracts * OPTION_MULTIPLIER,
      avgDailyVolume: sh.avgDailyVolume,
      marketCap: sh.marketCap,
      shortOpenInterest: sh.openInterest,
      shortProbItm: sh.probItm,
      currentHvPct: sh.hvPct,
      currentHvDte: sh.hvValue,
    };
  });

  // The strategy pages' probability figures, from the same endpoints.
  const today = todayETMidnight();
  const side = (t: VsTrade) => (t.spreadType === "bull_put" ? "put" : "call");
  const dteOf = (t: VsTrade) => Math.round((new Date(t.expirationDate + "T00:00:00").getTime() - today) / 86400000);
  const spotFor = (t: VsTrade, q: SpreadQuote | null) =>
    spotOf(t.symbol) ?? (q && q.underlyingPrice > 0 ? q.underlyingPrice : null) ?? (t.stockPriceAtEntry > 0 ? t.stockPriceAtEntry : null);
  const epItems: Batch[] = [];
  const entryItems: Batch[] = [];
  const ppItems: Batch[] = [];
  opens.forEach(({ trade: t }, i) => {
    if (!(t.shortStrikePrice > 0) || !t.expirationDate) return;
    const spot = spotFor(t, quotes[i]);
    const dte = Math.max(1, dteOf(t));
    if (spot != null && dte <= 400) {
      epItems.push({ key: contractKeyOf(t.symbol, t.shortOptionSymbol), ticker: t.symbol, dte, spot: Math.round(spot * 10) / 10, strike: t.shortStrikePrice, side: side(t) });
    }
    const entryDte = Math.round(t.dteAtEntry);
    if (t.stockPriceAtEntry > 0 && Number.isFinite(entryDte) && entryDte >= 1 && entryDte <= 400) {
      entryItems.push({ key: String(i), ticker: t.symbol, dte: entryDte, spot: t.stockPriceAtEntry, strike: t.shortStrikePrice, side: side(t) });
    }
    const ppDte = Math.max(0, dteOf(t));
    if (ppDte <= 504) ppItems.push({ key: String(i), ticker: t.symbol, dte: ppDte, strike: t.shortStrikePrice, side: side(t) });
  });
  // Black-Scholes P(ITM) from the live short mid and the index spot (the pages' live recompute).
  const bs = await Promise.all(opens.map(({ trade: t }, i) => {
    const q = quotes[i];
    const spot = spotFor(t, q);
    const dte = daysToExpirationBs(t.expirationDate);
    if (!q || !(q.shortMid > 0) || spot == null || !(dte > 0)) return Promise.resolve(null);
    return apiPost<{ probItm: number | null }>("/options/iv", {
      spot, strike: t.shortStrikePrice, daysToExpiration: dte, premium: q.shortMid,
      putCall: t.spreadType === "bull_put" ? "P" : "C",
    }).then((r) => r.probItm).catch(() => null);
  }));
  const [ep, entryEp, pp] = await Promise.all([
    batch("/p-itm/observed-european-batch", "p_hat", epItems),
    batch("/p-itm/observed-european-batch", "p_hat", entryItems),
    batch("/patterns/pitm-batch", "pItm", ppItems),
  ]);

  const spreads: HeldSpread[] = opens.map(({ trade: t, code, ledger, cfg, group }, i) => {
    const q = quotes[i];
    const tiers = cfg[LEDGERS.find((l) => l.id === ledger)!.tiers];
    return {
      trade: t,
      ledger,
      cfg,
      group,
      code,
      quote: q && bs[i] != null ? { ...q, shortProbItm: bs[i] } : q,
      // Close order must match both OCC roots, as on the strategy pages.
      closeOrder: orders.find((o) =>
        o.orderType === "VERTICAL_SPREAD_CLOSE"
        && occIdentity(o.optionSymbol) === occIdentity(t.shortOptionSymbol)
        && occIdentity(o.longOptionSymbol ?? "") === occIdentity(t.longOptionSymbol)) ?? null,
      targetPrice: calcBuybackTarget(t.netPremium, t.openedAt, tiers, t.expirationDate),
      entryEp: entryEp[String(i)] ?? null,
      ep: ep[contractKeyOf(t.symbol, t.shortOptionSymbol)] ?? null,
      pp: pp[String(i)] ?? null,
    };
  });
  const heldKeys = (short: boolean) =>
    new Set(broker.options.filter((o) => o.isShort === short).map((o) => contractKeyOf(o.underlying, o.optionSymbol)));
  return {
    account,
    accountValue: broker.accountValue,
    feeSchedule,
    orders,
    longHeld: heldKeys(false),
    shortHeld: heldKeys(true),
    spreads,
    options,
    equities: broker.equities,
  };
}

/** A live underlying last from the index feed, trying $PREFIX variants. */
function spotFrom(prices: Record<string, number>, undl: string): number | null {
  const direct = prices[undl];
  if (direct != null && direct > 0) return direct;
  const alt = undl.startsWith("$") ? prices[undl.slice(1)] : prices[`$${undl}`];
  return alt != null && alt > 0 ? alt : null;
}

function rowsOf(h: Held, spots: Record<string, number>): Row[] {
  const rows: Row[] = [];
  const hash = h.account.hashValue;
  for (const sp of h.spreads) {
    const { trade: t, code, quote: q } = sp;
    // Same arithmetic as the strategy pages' Open Vertical Spreads table.
    const midNet = q ? (q.netBid != null && q.netAsk != null ? (q.netBid + q.netAsk) / 2 : q.netMid) : null;
    const isPut = t.spreadType === "bull_put";
    const curDte = Math.max(0, Math.round((new Date(t.expirationDate + "T00:00:00").getTime() - Date.now()) / 86400000));
    const thetaEntry = netSpreadTheta(t.stockPriceAtEntry, t.shortStrikePrice, t.longStrikePrice, t.shortPremium, t.dteAtEntry, isPut, t.contracts);
    const thetaNow = netSpreadTheta(q?.underlyingPrice, t.shortStrikePrice, t.longStrikePrice, q?.shortMid, curDte, isPut, t.contracts);
    const curPItm = q?.shortProbItm ?? null;
    rows.push({
      key: `${hash}|${t.id}|${t.shortOptionSymbol}`,
      account: h.account,
      held: h,
      code,
      underlying: t.symbol,
      occRoot: occRootOf(t.shortOptionSymbol),
      isPut: t.spreadType === "bull_put",
      expDate: t.expirationDate,
      openedAt: t.openedAt,
      shortStrike: t.shortStrikePrice,
      longStrike: t.longStrikePrice,
      shortSymbol: t.shortOptionSymbol,
      longSymbol: t.longOptionSymbol,
      qty: t.netPremium > 0 ? -t.contracts : t.contracts,
      price: t.netPremium,
      quote: q,
      spread: sp,
      nearExit: midNet != null && sp.closeOrder?.price != null && midNet <= sp.closeOrder.price + 0.05,
      alert: curPItm != null && curPItm > 25 && thetaEntry != null && thetaNow != null && thetaNow > 2 * thetaEntry,
      mark: null,
      underlyingPrice: spotFrom(spots, t.symbol) ?? q?.underlyingPrice ?? null,
      income: midNet != null
        ? t.contracts * OPTION_MULTIPLIER * t.netPremium - t.contracts * OPTION_MULTIPLIER * midNet
        : null,
      premium: t.netPremium * OPTION_MULTIPLIER * t.contracts,
    });
  }
  for (const o of h.options) {
    const q = o.contracts;
    const cost = o.averagePrice * OPTION_MULTIPLIER * q;
    const isPut = o.putCall === "PUT";
    rows.push({
      key: `${hash}|${o.optionSymbol}`,
      account: h.account,
      held: h,
      code: `${o.isShort ? "S" : "L"}${isPut ? "P" : "C"}O`,
      underlying: o.underlying,
      occRoot: occRootOf(o.optionSymbol),
      isPut,
      expDate: o.expiration,
      openedAt: null,
      shortStrike: o.strike,
      longStrike: null,
      shortSymbol: o.optionSymbol,
      longSymbol: null,
      qty: o.isShort ? -q : q,
      price: o.averagePrice,
      quote: null,
      spread: null,
      nearExit: false,
      alert: false,
      mark: Math.abs(o.marketValue) / (OPTION_MULTIPLIER * q),
      underlyingPrice: spotFrom(spots, o.underlying),
      income: o.isShort ? cost + o.marketValue : o.marketValue - cost,
      premium: o.isShort ? cost : null,
    });
  }
  for (const e of h.equities) {
    rows.push({
      key: `${hash}|${e.symbol}`,
      account: h.account,
      held: h,
      code: e.shares < 0 ? "SEO" : "LEO",
      underlying: e.symbol,
      occRoot: "",
      isPut: null,
      expDate: null,
      openedAt: null,
      shortStrike: null,
      longStrike: null,
      shortSymbol: null,
      longSymbol: null,
      qty: e.shares,
      price: e.avgCost,
      quote: null,
      spread: null,
      nearExit: false,
      alert: false,
      mark: e.marketValue / e.shares,
      underlyingPrice: null,
      income: e.marketValue - e.avgCost * e.shares,
      premium: null,
    });
  }
  return rows;
}

const k = (v: number) => (v % 1 === 0 ? v.toFixed(0) : v.toFixed(1));

/** %OTM/(%ITM) of the short strike, as the strategy pages show it. */
function otmOf(r: Row): { node: React.ReactNode; cushion: number | null } {
  const px = r.underlyingPrice;
  if (r.shortStrike == null || r.isPut == null || px == null || px <= 0) return { node: "—", cushion: null };
  const mag = Math.abs((r.shortStrike - px) / px) * 100;
  const itm = r.isPut ? px < r.shortStrike : px > r.shortStrike;
  return {
    node: <span className={itm ? "text-red" : "text-green"}>{itm ? `(${mag.toFixed(1)}%)` : `${mag.toFixed(1)}%`}</span>,
    cushion: itm ? -mag : mag,
  };
}

/** Position sizing from the row's own strategy settings, as its page computes it. */
function sizesFor(ledger: LedgerId, cfg: Record<string, string>, accountValue: number) {
  const mod = ledger === "vs0" ? vs0Cfg : ledger === "vse" ? vseCfg : vsCfg;
  const sizes = mod.positionSizesFor(cfg, accountValue);
  return { size: (t: "bull_put" | "bear_call") => mod.positionSizeFor(sizes, t), sizes };
}

/** The "buy more" ticket for a held spread, built by its own strategy page's code. */
function seedFor(h: Held, sp: HeldSpread) {
  const isPut = sp.trade.spreadType === "bull_put";
  const { sizes } = sizesFor(sp.ledger, sp.cfg, h.accountValue);
  const base = { live: sp.quote, cfg: sp.cfg, positionSizes: sizes };
  if (sp.ledger === "vs0") return vs0BuyMore(sp.group, isPut, base);
  if (sp.ledger === "vse") return vseBuyMore(sp.group, isPut, { ...base, feeSchedule: h.feeSchedule });
  return vsBuyMore(sp.group, isPut, { ...base, feeSchedule: h.feeSchedule });
}

/** Legs that would close an existing position instead of opening — the order window warns. */
function legConflictFor(h: Held, seed: NonNullable<ReturnType<typeof seedFor>>) {
  const shortConflict = h.longHeld.has(contractKeyOf(seed.shortLeg.symbol, seed.shortLeg.optionSymbol));
  const longConflict = h.shortHeld.has(contractKeyOf(seed.longLeg.symbol, seed.longLeg.optionSymbol));
  if (!shortConflict && !longConflict) return null;
  return {
    shortConflict,
    longConflict,
    shortStrike: seed.shortLeg.strike,
    longStrike: seed.longLeg.strike,
    putCall: (seed.spreadType === "bull_put" ? "PUT" : "CALL") as "PUT" | "CALL",
    underlying: seed.shortLeg.symbol.replace(/^\$/, ""),
  };
}

/**
 * Every open position in every account, in the strategy pages' Open Vertical
 * Spreads format with the broker's logo first. Spreads carry the strategy
 * ledgers' entry prices and live quotes, so the numbers match those pages;
 * anything else the broker holds is shown at the broker's own prices.
 */
export function OpenPositions({
  accounts,
  openChart,
  header,
  hidden,
}: {
  accounts: UiAccount[];
  openChart: (accountHash: string) => (sym: string, overlay: SpreadChartOverlay | null) => void;
  /** The page's collapsible pane bar, with this pane's buttons on it. */
  header: (trailing: React.ReactNode) => React.ReactNode;
  hidden: boolean;
}) {
  const [held, setHeld] = useState<Held[] | null>(null);
  const [failed, setFailed] = useState<string[]>([]);
  const [bamlExpanded, setBamlExpanded] = useState(false);
  // Extra columns (separate P(ITM) figures, HV%, IV/HV, Code), as on the strategy pages.
  const [osExpanded, setOsExpanded] = useState(false);
  // Row click → that spread's P&L chart; P&L Profile → every open spread's payoff.
  const [selected, setSelected] = useState<{ sp: HeldSpread } | null>(null);
  const [showComposite, setShowComposite] = useState(false);
  const { marketData } = useMarketIndices();
  const { showToast } = useToast();
  const confirm = useConfirm();
  // The order windows, each opened for one row's account and strategy.
  const [buyMore, setBuyMore] = useState<{ held: Held; ledger: LedgerId; cfg: Record<string, string>; seed: NonNullable<ReturnType<typeof seedFor>> } | null>(null);
  const [closing, setClosing] = useState<{ held: Held; sp: HeldSpread; atMid: boolean } | null>(null);
  const [replacing, setReplacing] = useState<{ held: Held; order: OpenOrder; quote: SpreadQuote | null } | null>(null);
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  // Bumped after an order is placed, replaced or cancelled, to reload at once.
  const [reloadTick, setReloadTick] = useState(0);
  // The loader runs on a timer; it reads the latest index prices through this.
  const spotsRef = useRef<Record<string, number>>({});
  useEffect(() => {
    const next: Record<string, number> = {};
    for (const [sym, q] of Object.entries(marketData)) if (q.latest > 0) next[sym] = q.latest;
    spotsRef.current = next;
  }, [marketData]);

  useEffect(() => {
    if (accounts.length === 0) return;
    let cancelled = false;
    const load = async () => {
      const results = await Promise.allSettled(accounts.map((a) => loadHeld(a, (sym) => spotFrom(spotsRef.current, sym))));
      if (cancelled) return;
      // Keep the last good positions for an account whose refresh failed.
      setHeld((prev) => results.map((r, i) =>
        r.status === "fulfilled"
          ? r.value
          : prev?.find((h) => h.account.hashValue === accounts[i].hashValue)
            ?? {
              account: accounts[i], accountValue: 0, feeSchedule: scheduleForAccount(accounts[i].hashValue, null), orders: [],
              longHeld: new Set<string>(), shortHeld: new Set<string>(), spreads: [], options: [], equities: [],
            },
      ));
      setFailed(results.flatMap((r, i) => (r.status === "rejected" ? [accountNickname(accounts[i])] : [])));
    };
    load();
    const id = setInterval(() => { if (!document.hidden) load(); }, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [accounts, reloadTick]);

  const cancelOrder = async (ledger: LedgerId, o: OpenOrder) => {
    if (!(await confirmCancelOrder(confirm, o, { accountHash: o._accountHash, strategy: ledger, pane: "open-orders" }))) return;
    setCancellingId(o.orderId);
    try {
      await apiPost("/options/cancel", { accountHash: o._accountHash, tokenId: o._tokenId, orderId: o.orderId });
      showToast("success", "Order cancelled");
      setReloadTick((n) => n + 1);
    } catch {
      showToast("error", "Failed to cancel order");
    } finally {
      setCancellingId(null);
    }
  };
  const reload = () => setReloadTick((n) => n + 1);

  const spots: Record<string, number> = {};
  for (const [sym, q] of Object.entries(marketData)) if (q.latest > 0) spots[sym] = q.latest;
  const rows = (held ?? []).flatMap((h) => rowsOf(h, spots));
  const openSpreads = (held ?? []).flatMap((h) => h.spreads);
  const bar = header(
    <button
      onClick={(e) => { e.stopPropagation(); setShowComposite(true); }}
      disabled={openSpreads.length === 0}
      className="ml-3 text-xs font-medium px-3 py-0.5 rounded bg-cyan/20 text-cyan hover:bg-cyan/30 disabled:opacity-40 transition-colors"
      title="Composite P&L diagram across all open positions"
    >
      P&L Profile
    </button>,
  );
  const composite = (
    <CompositePnlModal
      open={showComposite}
      onClose={() => setShowComposite(false)}
      trades={openSpreads.map((sp) => sp.trade)}
      spotFor={(u, ts) => {
        const idx = spotFrom(spots, u);
        if (idx != null) return idx;
        for (const t of ts) {
          const q = openSpreads.find((sp) => sp.trade.shortOptionSymbol === t.shortOptionSymbol)?.quote;
          if (q && q.underlyingPrice > 0) return q.underlyingPrice;
        }
        return null;
      }}
    />
  );
  if (held == null || hidden || rows.length === 0) {
    return (
      <>
        {bar}
        {!hidden && (
          <div className="pt-3">
            {held == null ? (
              <div className="flex items-center gap-2 text-muted text-sm">
                <Spinner className={s.statusIcon} />
                Loading positions...
              </div>
            ) : rows.length === 0 && (
              <>
                {failed.length > 0 && <p className="text-xs text-red mb-2">Could not load positions for {failed.join(", ")}.</p>}
                <div className="inline-block rounded-lg border border-border bg-surface px-3 py-2 mb-[10px]">
                  <p className="text-xs text-muted">No open positions.</p>
                </div>
              </>
            )}
          </div>
        )}
        {composite}
      </>
    );
  }
  const failNote = failed.length > 0 && (
    <p className="text-xs text-red mb-2">Could not load positions for {failed.join(", ")}.</p>
  );

  const oc = (
    id: string,
    head: React.ReactNode,
    cell: (r: Row) => React.ReactNode,
    sortValue?: (r: Row) => number | string | null | undefined,
    expandedOnly?: boolean,
  ): TableColumn<Row> => ({
    id, header: () => head, cell, expandedOnly,
    ...(sortValue ? { sortKey: id, sortValue } : {}),
  });
  const blank = <td className="py-1 pr-4" />;

  const columns: TableColumn<Row>[] = [
    oc("broker", <th className="py-1 pr-4 underline text-center">Broker</th>, (r) => (
      <td className="p-0 pr-4 align-middle" title={accountNickname(r.account)}>
        <BrokerLogo account={r.account} className="block mx-auto h-4 w-4 object-contain rounded" />
      </td>
    ), (r) => accountNickname(r.account)),
    oc("expdate", expDateColumn.th(), (r) => (r.expDate ? expDateColumn.cell(r.code, r.expDate) : blank), (r) => r.expDate ?? "9999"),
    oc("symbol", symbolColumn.th(), (r) => {
      const bare = r.underlying.replace(/^\$/, "");
      const label = r.occRoot && r.occRoot !== bare ? `${r.underlying} ${r.occRoot}` : r.underlying;
      const overlay = r.isPut != null && r.shortStrike != null && r.longStrike != null
        ? makeSpreadOverlay({ isPut: r.isPut, shortStrike: r.shortStrike, longStrike: r.longStrike, entryTime: r.openedAt, expirationDate: r.expDate })
        : null;
      return symbolColumn.cell(r.code, label, () => openChart(r.account.hashValue)(r.underlying, overlay));
    }, (r) => r.underlying),
    oc("type", typeColumn.th(), (r) => (
      r.isPut == null
        ? <td className="py-1 pr-4 text-center">{kindTone(r.code, "Stock")}</td>
        : typeColumn.cell(r.code, r.isPut)
    ), (r) => (r.isPut == null ? "2_stock" : r.isPut ? "0_put" : "1_call")),
    oc("strikes", strikesColumn.th(), (r) => {
      if (r.shortStrike == null) return blank;
      if (r.longStrike == null) {
        return <td className="py-1 pr-4 text-center font-mono">{kindTone(r.code, k(r.shortStrike))}</td>;
      }
      return strikesColumn.cell(r.code, r.shortStrike, r.longStrike, r.underlyingPrice, r.shortSymbol, r.longSymbol);
    }, (r) => r.shortStrike),
    oc("otm", <th className="py-1 pr-4 underline text-center">%OTM<br />(ITM)</th>,
      (r) => <td className="py-1 pr-4 text-center font-mono">{otmOf(r).node}</td>,
      (r) => otmOf(r).cushion),
    oc("qty", qtyColumn.th(), (r) => (
      <td className="py-1 pr-4 text-center font-mono">{kindTone(r.code, qtyColumn.render({ kind: "plain", count: r.qty }))}</td>
    ), (r) => Math.abs(r.qty)),
    oc("agedte", ageDteColumn.th(), (r) => (
      r.openedAt && r.expDate
        ? <td className="py-1 pr-4 text-center">{kindTone(r.code, ageDteColumn.render(r.openedAt, r.expDate))}</td>
        : r.expDate ? <td className="py-1 pr-4 text-center">{kindTone(r.code, dteColumn.render(r.expDate))}</td> : blank
    ), (r) => r.expDate ?? "9999"),
    oc("price", priceColumn.th(), (r) => (
      <td className="py-1 pr-4 text-right font-mono">{kindTone(r.code, priceColumn.render({ kind: "n", value: r.price }))}</td>
    ), (r) => r.price),
    oc("baml", bamlColumn.th(bamlExpanded, () => setBamlExpanded((v) => !v)), (r) => (
      r.mark != null
        ? <td className="py-1 pr-4 text-center font-mono" title="Broker mark">{kindTone(r.code, r.mark.toFixed(2))}</td>
        : bamlColumn.cell(r.code, { bid: r.quote?.netBid ?? null, ask: r.quote?.netAsk ?? null, last: r.quote?.netLast ?? null, price: r.price }, bamlExpanded)
    )),
    oc("exitOrder", exitOrderColumn.th(), (r) => {
      if (!r.spread) return blank;
      const sp = r.spread;
      const h = r.held;
      const o = sp.closeOrder;
      const seed = seedFor(h, sp);
      return (
        <td className="py-1 pr-4 text-center">
          <span className="inline-flex items-center gap-1.5">
            {exitOrderColumn.render({
              limitPrice: o?.price ?? null,
              duration: o?.duration,
              onReplace: o ? () => setReplacing({ held: h, order: o, quote: sp.quote }) : undefined,
              onCancel: o ? () => cancelOrder(sp.ledger, o) : undefined,
              cancelling: o ? cancellingId === o.orderId : false,
              targetPrice: sp.targetPrice,
              onExit: () => setClosing({ held: h, sp, atMid: false }),
              onExitAtMid: () => setClosing({ held: h, sp, atMid: true }),
            })}
            <button
              disabled={!seed}
              onClick={(e) => { e.stopPropagation(); if (seed) setBuyMore({ held: h, ledger: sp.ledger, cfg: sp.cfg, seed }); }}
              aria-label="Add contracts"
              title={seed
                ? "Buy more — add contracts to this spread at the current credit"
                : "No live quote available for these legs yet"}
              className="inline-flex items-center justify-center p-0 m-0 leading-none hover:opacity-80 disabled:opacity-40 transition-opacity"
            >
              <CirclePlus className="w-3.5 h-3.5" />
            </button>
          </span>
        </td>
      );
    }, (r) => r.spread?.closeOrder?.price ?? r.spread?.targetPrice ?? null),
    // P(ITM): collapsed → one combined B/S/Emp/Pat column of current values;
    // expanded → the three columns, each with its entry/current pair.
    ...(osExpanded
      ? [
        oc("pitm", probItmColumn.th(), (r) => (r.spread
          ? <td className="py-1 pr-4 text-center font-mono">{renderPctPairColored(r.spread.trade.entryProbItm, r.spread.quote?.shortProbItm, false, (v) => v.toFixed(0), false, true, false)}</td>
          : blank), (r) => r.spread?.quote?.shortProbItm ?? null),
        oc("eitm", eProbItmColumn.th(), (r) => (r.spread
          ? <td className="py-1 pr-4 text-center font-mono">{renderPctPairColored(r.spread.entryEp, r.spread.ep, false, (v) => (v * 100).toFixed(0), false, true, false)}</td>
          : blank), (r) => r.spread?.ep ?? null),
        oc("ppitm", <th className="py-1 pr-4 underline text-center" title="Pattern P(ITM): short strike finishing ITM, from the cached daily-chart analog ensemble (warm via the Patterns page)">pP(ITM)</th>, (r) => (r.spread
          ? <td className="py-1 pr-4 text-center font-mono">{r.spread.pp != null ? `${(r.spread.pp * 100).toFixed(0)}%` : "—"}</td>
          : blank), (r) => r.spread?.pp ?? null),
      ]
      : [
        oc("itm", itmColumn.th(), (r) => {
          if (!r.spread) return blank;
          const sp = r.spread;
          return (
            <td className="py-1 pr-4 text-center font-mono">
              {renderPctPairColored(sp.trade.entryProbItm, sp.quote?.shortProbItm, false, (v) => v.toFixed(0), false, true, true)}
              {" "}<span className="text-foreground/50">/</span>{" "}
              {renderPctPairColored(sp.entryEp, sp.ep, false, (v) => (v * 100).toFixed(0), false, true, true)}
              {" "}<span className="text-foreground/50">/</span>{" "}
              {sp.pp != null ? `${(sp.pp * 100).toFixed(0)}%` : "—"}
            </td>
          );
        }, (r) => r.spread?.quote?.shortProbItm ?? null),
      ]),
    oc("hv", hvPctColumn.th(), (r) => (r.spread
      ? <td className="py-1 pr-4 text-center font-mono">{kindTone(r.code, renderPlainPair(r.spread.trade.entryHvPct ?? "—", r.spread.quote?.currentHvPct ?? "—", false))}</td>
      : blank), (r) => r.spread?.quote?.currentHvPct ?? null, true),
    oc("ivhv", ivHvColumn.th(), (r) => {
      if (!r.spread) return blank;
      const t = r.spread.trade;
      const q = r.spread.quote;
      const entry = t.entryIv != null && t.entryHvDte != null && t.entryHvDte > 0 ? (t.entryIv / t.entryHvDte).toFixed(2) : null;
      const live = q?.shortIv != null && q?.currentHvDte != null && q.currentHvDte > 0 ? (q.shortIv / q.currentHvDte).toFixed(2) : null;
      return <td className="py-1 pr-4 text-center font-mono">{kindTone(r.code, renderPlainPair(entry ?? "—", live ?? "—", false))}</td>;
    }, (r) => {
      const q = r.spread?.quote;
      return q?.shortIv != null && q?.currentHvDte != null && q.currentHvDte > 0 ? q.shortIv / q.currentHvDte : null;
    }, true),
    oc("incprem", incPremColumn.th(), (r) => incPremColumn.cell(r.code, r.income, r.premium), (r) => r.income),
    oc("code", codeColumn.th(), (r) => codeColumn.cell(r.code), (r) => r.code, true),
    oc("expander",
      <th className="pt-1 pb-1 pr-4 text-center">
        <button
          onClick={() => setOsExpanded((e) => !e)}
          className="text-foreground/60 hover:text-foreground"
          title={osExpanded ? "Hide extra columns" : "Show extra columns"}
        >
          {osExpanded ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
      </th>,
      () => <td className="py-0.5 pr-4" />),
  ];

  return (
    <>
      {bar}
      <div className="pt-3">
      {failNote}
      <div className="inline-block rounded-lg border border-red bg-surface px-3 pb-2 m-2.5 overflow-x-auto">
        <ColumnTable<Row>
          columns={columns}
          rows={rows}
          expanded={osExpanded}
          defaultSortKey="expdate"
          defaultSortAsc
          tableClassName="text-xs whitespace-nowrap"
          headRowClassName="text-left text-amber border-b border-border"
          renderRow={(r, cells) => {
            const sp = r.spread;
            const isSelected = !!sp && selected?.sp.group.shortLeg.optionSymbol === sp.group.shortLeg.optionSymbol
              && selected.sp.trade.accountHash === sp.trade.accountHash;
            return (
              <tr
                key={r.key}
                onClick={sp ? () => setSelected(isSelected ? null : { sp }) : undefined}
                className={`border-t-2 border-border border-b border-border/50 ${sp ? "cursor-pointer" : ""} ${r.alert ? "bg-red/30" : r.nearExit ? "bg-green/20" : isSelected ? "bg-surface-light" : "bg-surface-light/30 hover:bg-surface-light"}`}
              >
                {cells}
              </tr>
            );
          }}
        />
      </div>
      </div>
      {/* The selected spread's P&L at expiration, as on the strategy pages. */}
      <Modal open={!!selected} onClose={() => setSelected(null)} width="655px">
        {selected && (() => {
          const g = selected.sp.group;
          const netCredit = g.trade.netPremium;
          const qty = g.shortLeg.contracts;
          const mktVal = g.shortLeg.marketValue + g.longLeg.marketValue;
          const q = selected.sp.quote;
          return (
            <SpreadPnlChart
              underlying={g.underlying}
              putCall={g.putCall}
              shortStrike={g.shortLeg.strike}
              longStrike={g.longLeg.strike}
              width={g.width}
              netCredit={netCredit}
              contracts={qty}
              currentPrice={q?.underlyingPrice ?? null}
              currentMid={q?.netMid ?? null}
              currentPnl={netCredit * qty * OPTION_MULTIPLIER + mktVal}
              onClose={() => setSelected(null)}
            />
          );
        })()}
      </Modal>
      {composite}
      {buyMore && (() => {
        const { held: h, ledger, cfg, seed } = buyMore;
        const sizes = sizesFor(ledger, cfg, h.accountValue);
        const props = {
          account: { hash: h.account.hashValue, tokenId: h.account.tokenId, label: strategyAccountName(h.account) },
          legConflict: legConflictFor(h, seed),
          onClose: () => setBuyMore(null),
          onSuccess: reload,
        };
        if (ledger === "vs0") return <vs0Modals.SpreadOrderModal {...props} spread={seed as Parameters<typeof vs0Modals.SpreadOrderModal>[0]["spread"]} positionSize={sizes.size(seed.spreadType)} />;
        if (ledger === "vse") return <vseModals.SpreadOrderModal {...props} spread={seed as Parameters<typeof vseModals.SpreadOrderModal>[0]["spread"]} positionSize={sizes.size(seed.spreadType)} />;
        return <vsModals.SpreadOrderModal {...props} spread={seed as Parameters<typeof vsModals.SpreadOrderModal>[0]["spread"]} positionSize={sizes.size(seed.spreadType)} />;
      })()}
      {closing && (() => {
        const { held: h, sp, atMid } = closing;
        const tiers = sp.cfg[LEDGERS.find((l) => l.id === sp.ledger)!.tiers];
        const props = {
          account: h.account,
          spread: sp.group,
          decayPct: Number((tiers ?? "0:75").split(",")[0]?.split(":")[1] ?? 75),
          seedAtMid: atMid,
          targetPriceOverride: sp.targetPrice,
          // Replace, not add: cancel any working exit order on these legs first.
          beforePlace: async () => {
            const short = sp.group.shortLeg.optionSymbol;
            const long = sp.group.longLeg.optionSymbol;
            for (const o of h.orders.filter((x) => isWorkingVsCloseOrder(x, short, long))) {
              await apiPost("/options/cancel", { accountHash: o._accountHash, tokenId: o._tokenId, orderId: o.orderId });
            }
          },
          onClose: () => setClosing(null),
          onSuccess: reload,
        };
        if (sp.ledger === "vs0") return <vs0Modals.CloseSpreadModal {...props} trade={sp.trade} liveData={sp.quote} />;
        if (sp.ledger === "vse") return <vseModals.CloseSpreadModal {...props} trade={sp.trade as unknown as VseTrade} liveData={sp.quote} />;
        return <vsModals.CloseSpreadModal {...props} trade={sp.trade} liveData={sp.quote} />;
      })()}
      {replacing && (
        <ReplaceOrderModal
          account={replacing.held.account}
          order={replacing.order as unknown as Parameters<typeof ReplaceOrderModal>[0]["order"]}
          livePrice={replacing.quote?.netMid ?? null}
          liveBid={replacing.quote?.netBid ?? null}
          liveAsk={replacing.quote?.netAsk ?? null}
          onClose={() => setReplacing(null)}
          onSuccess={reload}
        />
      )}
    </>
  );
}
